/**
 * Deterministically match an optimizer suggestion to a later completed EDIT.
 *
 * New apply messages carry an exact source-message/index reference. Older or
 * manually-created EDITs may not, so the fallback compares the mutation itself:
 * structured field operations when present, otherwise the generated edit
 * sentence plus every target entity, action and recommended value. There is no
 * fuzzy text or LLM judgement in this path; uncertainty remains "not applied".
 */

import { resolveOptimizationSuggestions } from './optimizationSuggestions';
import { buildSuggestionEditRequest } from './optimizerSuggestionApply';

const EDIT_ACTION = 'edit-campaign';
const COMPLETE_STATUSES = new Set(['completed', 'success']);

const parseObject = (value) => {
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
};

const text = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ');

const timeOf = (message, createdOnly = false) => {
  const raw = createdOnly
    ? (message?.message_created_at || message?.associated_at)
    : (message?.message_timestamp || message?.message_created_at || message?.associated_at);
  const millis = new Date(raw).getTime();
  return Number.isFinite(millis) ? millis : null;
};

const metadataOf = (message) => {
  const file = parseObject(message?.file_metadata);
  // Campaign activity rows are BOT messages, while the edit dispatch metadata
  // (source, optimizeMessageId, suggestionIndex and config_payload) is stored on
  // the USER message that triggered the bot. getCampaignMessages exposes that
  // joined row as source_file_metadata. On a reload the bot's file_metadata is
  // normally only { frontendUrl }, so ignoring the source metadata discards the
  // exact application reference and the structured edit payload.
  const sourceFile = parseObject(message?.source_file_metadata);
  const response = parseObject(message?.json_response);
  return {
    ...(parseObject(response?.metadata) || {}),
    ...(file || {}),
    ...(sourceFile || {}),
  };
};

const normalizedValue = (value) => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return `[${value.map(normalizedValue).sort().join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${text(key)}:${normalizedValue(value[key])}`).join(',')}}`;
  }
  return text(value);
};

const editSignature = (edit) => normalizedValue({
  field: edit?.field,
  operation: edit?.operation,
  value: edit?.value,
  newValue: edit?.newValue ?? edit?.new_value,
  targetEntities: (edit?.targetEntities || edit?.target_entities || []).map(String).sort(),
});

const structuredSignatures = (configPayload) => {
  const config = parseObject(configPayload);
  if (!config) return [];
  const batch = parseObject(config.batch);
  if (Array.isArray(batch?.edits)) return batch.edits.map(editSignature).sort();
  // Typed Google Ads operations are executed verbatim. Their complete payload
  // is the action/entity/value contract, so equality must be complete too.
  if (config.operation && config.payload) return [`config:${normalizedValue(config)}`];
  return [];
};

const leaves = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(leaves);
  if (typeof value === 'object') return Object.values(value).flatMap(leaves);
  const normalized = text(value);
  return normalized ? [normalized] : [];
};

const suggestionsOf = (message) => {
  const details = parseObject(message?.json_response)?.details;
  const summary = Array.isArray(details?.actions) ? details.actions : null;
  return summary?.length ? summary : resolveOptimizationSuggestions(details);
};

const suggestionAt = (message, index) => (Number.isInteger(Number(index))
  ? suggestionsOf(message)[Number(index)]
  : null);

const resultFor = (edit, matchedBy) => ({
  messageId: edit.message_id,
  appliedAt: edit.message_timestamp || edit.message_created_at || edit.associated_at || null,
  matchedBy,
});

/**
 * @returns {{messageId:string, appliedAt:string|null, matchedBy:string}|null}
 */
export function findSuggestionApplication(messages, optimizeMessageId, suggestionIndex, {
  destination = null, platformCampaignId = null,
} = {}) {
  const source = (messages || []).find((message) => String(message.message_id) === String(optimizeMessageId));
  const suggestion = suggestionAt(source, suggestionIndex);
  if (!source || !suggestion) return null;

  const sourceCreated = timeOf(source, true);
  const candidates = (messages || [])
    .filter((message) => {
      const action = text(message.action || message.message_type);
      if (action !== text(EDIT_ACTION) || !COMPLETE_STATUSES.has(text(message.status))) return false;
      const editCreated = timeOf(message, true);
      return sourceCreated === null || editCreated === null || editCreated > sourceCreated;
    })
    // The activity API is newest-first today, but matching must not depend on
    // transport order. Report when the change was first known to be applied.
    .sort((a, b) => (timeOf(a, true) ?? Number.MAX_SAFE_INTEGER)
      - (timeOf(b, true) ?? Number.MAX_SAFE_INTEGER));

  // An exact persisted reference is authoritative, but only after the EDIT has
  // completed and only when it is later than the recommendation.
  for (const edit of candidates) {
    const metadata = metadataOf(edit);
    if (metadata.source === 'apply_actionable_suggestion'
      && String(metadata.optimizeMessageId) === String(optimizeMessageId)
      && Number(metadata.suggestionIndex) === Number(suggestionIndex)) {
      return resultFor(edit, 'source-reference');
    }
  }

  const built = buildSuggestionEditRequest(suggestion, { destination, platformCampaignId });
  if (built.error) return null;

  const wantedStructured = built.edits?.map(editSignature).sort()
    || structuredSignatures(built.configPayload);
  if (wantedStructured.length) {
    for (const edit of candidates) {
      const actual = structuredSignatures(metadataOf(edit).config_payload);
      if (wantedStructured.every((signature) => actual.includes(signature))) {
        return resultFor(edit, 'structured-edit');
      }
    }
    return null;
  }

  // Conservative legacy fallback. All four parts must be present: the exact
  // generated description, action, every entity, and every recommended value.
  const description = text(built.description);
  const action = text(suggestion.action_type || suggestion.action);
  const entities = (built.targetEntities || []).map(text).filter(Boolean);
  const values = leaves(built.suggestedValues ?? suggestion.recommended_value ?? suggestion.new_value);
  if (!description || !action || !entities.length || !values.length) return null;

  for (const edit of candidates) {
    const content = text(edit.content);
    if (content.includes(description)
      && content.includes(action)
      && entities.every((entity) => content.includes(entity))
      && values.every((value) => content.includes(value))) {
      return resultFor(edit, 'exact-edit-content');
    }
  }
  return null;
}

/**
 * Every suggestion index of one run that a later, completed edit applied —
 * findSuggestionApplication over the run's own suggestion list. The Runs tab
 * and the Optimization agent read their "Applied" state through this, so all
 * three surfaces agree on what applied means.
 *
 * @returns {number[]} ascending; [] when the run is not in `messages`
 */
export function appliedSuggestionIndexes(messages, optimizeMessageId, opts = {}) {
  const source = (messages || []).find((m) => String(m.message_id) === String(optimizeMessageId));
  if (!source) return [];
  const out = [];
  suggestionsOf(source).forEach((_, index) => {
    if (findSuggestionApplication(messages, optimizeMessageId, index, opts)) out.push(index);
  });
  return out;
}

const suggestionApplication = { findSuggestionApplication, appliedSuggestionIndexes };

export default suggestionApplication;
